'use no memo';

import { User, type UsersSort } from '@kalos/kalos-rpc';
import {
  Button,
  DataTable,
  DataTableColumnHeader,
  Form,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kalos/ui';
import { MagnifyingGlassIcon, Pencil1Icon, TrashIcon } from '@radix-ui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  type ColumnDef,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME } from '../../../../constants';
import { useAuth } from '../../../../context/AuthContext';
import { queryKeys } from '../../../../hooks/react-query/constants';
import { useBatchUserQuery, type UserFilters } from '../../../../hooks/react-query/useUserQuery';
import { useMobileDevice } from '../../../../hooks/useMedia';
import { debounce, UserClientService } from '../../../../tools/helpers';
import { formatPhoneNumber } from '../../../../utils/formatPhoneNumber';
import { CustomerEditDialog } from '../../../CustomerDetailsNew/components/CustomerEditDialog';
import { ConfirmDelete } from '../../ConfirmDelete';
import { type AdvancedSearchProps } from '..';
import { filtersKey } from '../listState';
import { useAdvancedSearchListState } from '../paginationStore';
import CustomerSearchForm from './CustomerSearchForm';
import { CustomersSearchPhone } from './CustomersSearchPhone';
import { type UserSearchSchemeType, useUserSearchForm } from './utils';

const loadingCustomers: User[] = Array.from({ length: 5 }, (_, i) => User.create());

type CustomersSearchProps = Pick<AdvancedSearchProps, 'editableCustomers' | 'deletableCustomers'>;
export const CustomersSearch = ({
  editableCustomers,
  deletableCustomers,
}: CustomersSearchProps) => {
  const { page, setPage, setTotalEntriesCount, sort, setSort } = useAdvancedSearchListState();
  const navigate = useNavigate();

  const userSearchForm = useUserSearchForm();

  const [customerFilter, setCustomerFilter] = useState<UserSearchSchemeType>(() => {
    return userSearchForm.getValues();
  });

  // The page resets only when the filters really change, not when the form first loads from
  // the URL, so a reload keeps the page.
  const appliedFiltersKey = useRef(filtersKey(customerFilter));
  useEffect(() => {
    const debouncedUpdate = debounce((data: UserSearchSchemeType) => {
      const nextKey = filtersKey(data);
      if (nextKey === appliedFiltersKey.current) return;
      appliedFiltersKey.current = nextKey;
      setPage(0);
      setCustomerFilter(data);
    }, FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME);
    const updateSubscription = userSearchForm.watch(debouncedUpdate);
    return () => updateSubscription.unsubscribe();
  }, [setPage, userSearchForm]);

  const customersSort = useMemo<UsersSort>(
    () =>
      sort
        ? {
            orderByField: sort.id as keyof User,
            orderBy: `user_${sort.id}`,
            orderDir: sort.desc ? 'DESC' : 'ASC',
          }
        : { orderByField: 'lastname', orderBy: 'user_lastname', orderDir: 'ASC' },
    [sort],
  );

  const customerFilterCriteria = useMemo<UserFilters>(() => {
    return {
      orderBy: customersSort.orderBy,
      orderDir: customersSort.orderDir,
      pageNumber: page,
      firstname: customerFilter.firstname ? `%${customerFilter.firstname}%` : undefined,
      lastname: customerFilter.lastname ? `%${customerFilter.lastname}%` : undefined,
      businessname: customerFilter.businessname ? `%${customerFilter.businessname}%` : undefined,
      email: customerFilter.email ? `%${customerFilter.email}%` : undefined,
      phone: customerFilter.phone ? `%${customerFilter.phone}%` : undefined,
      employeeDepartmentID: 0,
      isEmployee: 0,
      isActive: 1,
      fieldMask: ['IsEmployee'],
    };
  }, [
    customerFilter.businessname,
    customerFilter.email,
    customerFilter.firstname,
    customerFilter.lastname,
    customerFilter.phone,
    customersSort.orderBy,
    customersSort.orderDir,
    page,
  ]);

  const isMobileDevice = useMobileDevice();

  // Phones load page after page as the list scrolls (CustomersSearchPhone), so the paged query
  // is the desktop table's only.
  const customersQuery = useBatchUserQuery({
    filters: customerFilterCriteria,
    enabled: !isMobileDevice,
  });

  const queryClient = useQueryClient();

  const onRowClick = useCallback(
    (user: User) => {
      if (!user.id) return;
      navigate({ to: '/customers/$customerId', params: { customerId: user.id } });
    },
    [navigate],
  );

  useEffect(() => {
    if (customersQuery.isSuccess) {
      setTotalEntriesCount(customersQuery.data?.totalCount || 0);
    }
  }, [customersQuery.data?.totalCount, customersQuery.isSuccess, setTotalEntriesCount]);

  const [pendingCustomerEditing, setPendingCustomerEditing] = useState<User | null>(null);
  const handlePendingCustomerEditingToggle = useCallback(
    (pendingCustomerEditing: User | null) => () =>
      setPendingCustomerEditing(pendingCustomerEditing),
    [setPendingCustomerEditing],
  );

  const [pendingCustomerDeleting, setPendingCustomerDeleting] = useState<User | null>(null);
  const handlePendingCustomerDeletingToggle = useCallback(
    (pendingCustomerDeleting: User | null) => () =>
      setPendingCustomerDeleting(pendingCustomerDeleting),
    [setPendingCustomerDeleting],
  );

  const auth = useAuth();

  const isManager = useMemo(() => {
    return auth.user.permissionGroups.some(
      (permission) => permission.type === 'role' && permission.name === 'Manager',
    );
  }, [auth.user.permissionGroups]);

  const customersTableColumns = useMemo<ColumnDef<User>[]>(() => {
    const columns: ColumnDef<User>[] = [
      {
        accessorKey: 'firstname',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="First Name" column={column} />
        ),
        cell({ row }) {
          const firstName = row.getValue('firstname');
          return <div className="w-32">{String(firstName)}</div>;
        },
        sortingFn: () => 0,
      },
      {
        accessorKey: 'lastname',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Last Name" column={column} />
        ),
        cell({ row }) {
          const lastname = row.getValue('lastname');
          return <div className="w-32">{String(lastname)}</div>;
        },
        sortingFn: () => 0,
      },
      {
        accessorKey: 'businessname',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Business Name" column={column} />
        ),
        cell({ row }) {
          const lastname = row.getValue('businessname');
          return <div className="w-32">{String(lastname)}</div>;
        },
        sortingFn: () => 0,
      },
      {
        accessorKey: 'phone',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Primary Phone" column={column} />
        ),
        cell({ row }) {
          const phone = row.getValue('phone') as string;
          return <div className="w-32">{phone ? formatPhoneNumber(phone) : phone}</div>;
        },
        sortingFn: () => 0,
      },
      {
        accessorKey: 'email',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Email" column={column} />
        ),
        cell({ row }) {
          const email = row.getValue('email');
          return <div className="w-32 break-all">{String(email)}</div>;
        },
        sortingFn: () => 0,
      },
      {
        accessorKey: 'address',
        header: ({ column }) => (
          <DataTableColumnHeader hideVisibilityToggle title="Address" column={column} />
        ),
        cell({ row }) {
          const user = row.original;
          const address =
            `${user.address || ''}, ${user.city || ''}, ${user.state || ''} ${user.zip || ''}`
              .trim()
              .replace(/,\s*$/, '');
          return <div className="w-48 break-words">{address}</div>;
        },
        sortingFn: () => 0,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell({ row }) {
          const entry = row.original;
          return (
            <div className="flex w-32 gap-2">
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      className="px-2"
                      asChild
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <Link to="/customers/$customerId" params={{ customerId: row.original.id }}>
                        <MagnifyingGlassIcon />
                      </Link>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="px-2 py-1">View customer</TooltipContent>
                </Tooltip>
                {editableCustomers && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        className="px-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingCustomerEditing(entry);
                        }}
                      >
                        <Pencil1Icon />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="px-2 py-1">Edit customer</TooltipContent>
                  </Tooltip>
                )}
                {deletableCustomers && isManager && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        title="Delete"
                        variant="outline"
                        className="px-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingCustomerDeleting(entry);
                        }}
                      >
                        <TrashIcon />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="px-2 py-1">Delete customer</TooltipContent>
                  </Tooltip>
                )}
              </TooltipProvider>
            </div>
          );
        },
      },
    ];
    return columns;
  }, [deletableCustomers, editableCustomers, isManager]);

  const sorting = useMemo<SortingState>(() => (sort ? [sort] : []), [sort]);

  const onSortChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      const newState = typeof updater === 'function' ? updater(sorting) : updater;
      setSort(newState[0] ? { id: newState[0].id, desc: newState[0].desc } : null);
    },
    [setSort, sorting],
  );

  const skeletonColumns = useMemo<ColumnDef<User>[]>(() => {
    return customersTableColumns.map((column) => ({
      ...column,
      cell: () => <Skeleton className="bg-foreground/30 h-4 w-32" />,
    }));
  }, [customersTableColumns]);

  const table = useReactTable({
    data: customersQuery.isPending ? loadingCustomers : customersQuery.data?.results ?? [],
    columns: customersQuery.isPending ? skeletonColumns : customersTableColumns,
    state: {
      sorting,
      pagination: {
        pageIndex: 0,
        pageSize: 25,
      },
    },
    onSortingChange: onSortChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  const onSaveCustomer = useCallback(() => {
    setPendingCustomerEditing(null);

    queryClient.refetchQueries({ queryKey: [queryKeys.user.root, queryKeys.user.list] });
  }, [queryClient]);

  const handleDeleteCustomer = useCallback(async () => {
    if (pendingCustomerDeleting) {
      const id = pendingCustomerDeleting.id;
      setPendingCustomerDeleting(null);
      await UserClientService.deleteUserById(id);
      queryClient.refetchQueries({ queryKey: [queryKeys.user.root, queryKeys.user.list] });
    }
  }, [pendingCustomerDeleting, queryClient]);

  if (isMobileDevice) {
    return (
      <>
        <CustomersSearchPhone
          form={userSearchForm}
          filterCriteria={customerFilterCriteria}
          sort={sort}
          onSortChange={setSort}
          onDelete={deletableCustomers && isManager ? setPendingCustomerDeleting : undefined}
        />
        {pendingCustomerEditing && (
          <CustomerEditDialog
            open
            mode="edit"
            customer={pendingCustomerEditing}
            onClose={handlePendingCustomerEditingToggle(null)}
            onSave={onSaveCustomer}
          />
        )}
        {pendingCustomerDeleting && (
          <ConfirmDelete
            open
            onClose={handlePendingCustomerDeletingToggle(null)}
            onConfirm={handleDeleteCustomer}
            kind="Customer"
            name={UserClientService.getCustomerNameAndBusinessName(pendingCustomerDeleting)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="p-4">
        <Form {...userSearchForm}>
          <CustomerSearchForm />
        </Form>
      </div>

      <div className="max-w-full overflow-x-auto">
        <DataTable onRowClick={onRowClick} table={table} />
      </div>

      {pendingCustomerEditing && (
        <CustomerEditDialog
          open
          mode="edit"
          customer={pendingCustomerEditing}
          onClose={handlePendingCustomerEditingToggle(null)}
          onSave={onSaveCustomer}
        />
      )}
      {pendingCustomerDeleting && (
        <ConfirmDelete
          open
          onClose={handlePendingCustomerDeletingToggle(null)}
          onConfirm={handleDeleteCustomer}
          kind="Customer"
          name={UserClientService.getCustomerNameAndBusinessName(pendingCustomerDeleting)}
        />
      )}
    </>
  );
};
